/**
 * Lead Capture Components
 * 
 * - Exit Intent Popup
 * - Smart Lead Form  
 * - Floating CTA Button
 * - Inline Lead Magnets
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { X, ArrowRight, Mail, User, Building2, Phone, Sparkles, CheckCircle } from 'lucide-react';
import analyticsService from '../services/analyticsService';

// ============================================================================
// EXIT INTENT POPUP
// ============================================================================

export const ExitIntentPopup = ({
  title,
  subtitle,
  ctaText,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const resolvedTitle = title || t('leadCapture.exitTitle');
  const resolvedSubtitle = subtitle || t('leadCapture.exitSubtitle');
  const resolvedCtaText = ctaText || t('leadCapture.exitCta');
  const [isVisible, setIsVisible] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    // Only show on public/marketing pages, NOT on authenticated dashboard pages or login pages
    const publicPaths = ['/', '/pricing', '/features', '/solutions', '/resources', '/partner', '/contact', '/privacy-policy', '/security-compliance', '/screenshot-monitoring', '/productivity-analytics', '/compliance-suite', '/insider-risk', '/tryus', '/compare'];
    const pathname = window.location.pathname;

    // Never show on login/signin pages or authenticated dashboard pages
    if (pathname.includes('/signin') ||
        pathname.includes('/signup') ||
        pathname.includes('/login') ||
        pathname.includes('/super-admin') ||
        pathname.includes('/dashboard') ||
        pathname.startsWith('/employee-') ||
        pathname.startsWith('/billing') ||
        pathname.startsWith('/settings') ||
        pathname.startsWith('/alerts') ||
        pathname.startsWith('/setup')) {
      return;
    }
    const isPublicPage = publicPaths.includes(pathname) || pathname === '/' || pathname.startsWith('/tryus') || pathname.startsWith('/features/') || pathname.startsWith('/resources/');

    // Don't show on authenticated pages (dashboard, settings, etc.)
    if (!isPublicPage) {
      return;
    }

    // Check if already shown this session
    if (sessionStorage.getItem('exit_popup_shown')) {
      setHasShown(true);
      return;
    }

    const handleMouseLeave = (e) => {
      // Trigger when mouse moves to top of page (exit intent)
      if (e.clientY < 10 && !hasShown && !isVisible) {
        setIsVisible(true);
        setHasShown(true);
        sessionStorage.setItem('exit_popup_shown', 'true');
        analyticsService.track('Exit Intent Shown', { page: window.location.pathname });
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [hasShown, isVisible]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    analyticsService.trackFormComplete('Exit Intent Popup', { email });

    try {
      const response = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          source: 'exit_intent',
          page: window.location.pathname,
          visitorId: analyticsService.visitorId,
          leadScore: analyticsService.getLeadScore(),
        }),
      });

      if (response.ok) {
        setIsSuccess(true);
        onSubmit?.({ email });
      }
    } catch (error) {
      console.error('Lead capture error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fadeIn">
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md mx-4 p-8 animate-slideUp">
        <button
          onClick={() => setIsVisible(false)}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:text-gray-200 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {isSuccess ? (
          <div className="text-center py-4">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('leadCapture.allSet')}</h3>
            <p className="text-gray-600 dark:text-gray-200">{t('leadCapture.reachOut24h')}</p>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-amber-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{resolvedTitle}</h3>
              <p className="text-gray-600 dark:text-gray-200 text-sm">{resolvedSubtitle}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 dark:text-gray-300" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => analyticsService.trackFormStart('Exit Intent Popup')}
                  placeholder={t('leadCapture.workEmailPlaceholder')}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none transition-all"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-medium rounded-lg hover:from-orange-600 hover:to-amber-600 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isSubmitting ? t('common.sending') : resolvedCtaText}
                <ArrowRight className="w-4 h-4" />
              </button>
            </form>

            <p className="text-xs text-gray-500 dark:text-gray-300 text-center mt-4">
              {t('leadCapture.noSpam')}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// SMART LEAD FORM (Multi-step with progressive profiling)
// ============================================================================

export const SmartLeadForm = ({
  formId = 'main_lead_form',
  onSuccess,
  showPhone = true,
  showCompany = true,
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    company: '',
    phone: '',
    employees: '',
    interest: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    analyticsService.trackFormComplete(formId, formData);
    analyticsService.identify(formData.email, formData);

    try {
      const response = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          source: formId,
          page: window.location.pathname,
          visitorId: analyticsService.visitorId,
          leadScore: analyticsService.getLeadScore(),
          visitorData: analyticsService.getVisitorData(),
        }),
      });

      if (response.ok) {
        setIsSuccess(true);
        onSuccess?.(formData);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="text-center py-8 px-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl border border-green-200">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('leadCapture.thankYou', { name: formData.name?.split(' ')[0] || t('leadCapture.there') })}</h3>
        <p className="text-gray-600 dark:text-gray-200">{t('leadCapture.teamReachOut')}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Step 1: Email */}
      {step >= 1 && (
        <div className="relative animate-fadeIn">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 dark:text-gray-300" />
          <input
            type="email"
            value={formData.email}
            onChange={(e) => updateField('email', e.target.value)}
            onFocus={() => analyticsService.trackFormStart(formId)}
            onBlur={() => formData.email && setStep(Math.max(step, 2))}
            placeholder={t('leadCapture.workEmailRequired')}
            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
            required
          />
        </div>
      )}

      {/* Step 2: Name */}
      {step >= 2 && (
        <div className="relative animate-fadeIn">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 dark:text-gray-300" />
          <input
            type="text"
            value={formData.name}
            onChange={(e) => updateField('name', e.target.value)}
            onBlur={() => formData.name && setStep(Math.max(step, 3))}
            placeholder={t('leadCapture.fullNameRequired')}
            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
            required
          />
        </div>
      )}

      {/* Step 3: Company */}
      {step >= 3 && showCompany && (
        <div className="relative animate-fadeIn">
          <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 dark:text-gray-300" />
          <input
            type="text"
            value={formData.company}
            onChange={(e) => updateField('company', e.target.value)}
            onBlur={() => formData.company && setStep(Math.max(step, 4))}
            placeholder={t('leadCapture.companyName')}
            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
          />
        </div>
      )}

      {/* Step 4: Phone (Optional) */}
      {step >= 4 && showPhone && (
        <div className="relative animate-fadeIn">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 dark:text-gray-300" />
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => updateField('phone', e.target.value)}
            placeholder={t('leadCapture.phoneOptional')}
            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
          />
        </div>
      )}

      {/* Company Size (shows after company) */}
      {step >= 3 && (
        <select
          value={formData.employees}
          onChange={(e) => updateField('employees', e.target.value)}
          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none text-gray-600 dark:text-gray-200 animate-fadeIn"
        >
          <option value="">{t('leadCapture.companySize')}</option>
          <option value="1-10">1-10 {t('leadCapture.employees')}</option>
          <option value="11-50">11-50 {t('leadCapture.employees')}</option>
          <option value="51-200">51-200 {t('leadCapture.employees')}</option>
          <option value="201-500">201-500 {t('leadCapture.employees')}</option>
          <option value="500+">500+ {t('leadCapture.employees')}</option>
        </select>
      )}

      <button
        type="submit"
        disabled={isSubmitting || !formData.email || !formData.name}
        className="w-full py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-medium rounded-lg hover:from-orange-600 hover:to-amber-600 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSubmitting ? t('common.sending') : t('leadCapture.getStartedFree')}
        <ArrowRight className="w-4 h-4" />
      </button>

      <p className="text-xs text-gray-500 dark:text-gray-300 text-center">
        {t('leadCapture.privacyAgreement')}
      </p>
    </form>
  );
};

// ============================================================================
// FLOATING CTA BUTTON
// ============================================================================

export const FloatingCTA = ({
  text = "Get a Demo",
  onClick,
  showAfterScroll = 300,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isPublicPage, setIsPublicPage] = useState(false);

  useEffect(() => {
    // Only show on public/marketing pages, NOT on authenticated dashboard pages
    const publicPaths = ['/', '/pricing', '/features', '/solutions', '/resources', '/partner', '/contact', '/privacy-policy', '/security-compliance', '/screenshot-monitoring', '/productivity-analytics', '/compliance-suite', '/insider-risk', '/tryus', '/compare'];
    const pathname = window.location.pathname;

    // Explicitly exclude authenticated paths
    if (pathname.includes('/dashboard') ||
        pathname.includes('/signin') ||
        pathname.includes('/signup') ||
        pathname.includes('/login') ||
        pathname.includes('/super-admin') ||
        pathname.startsWith('/employee-') ||
        pathname.startsWith('/billing') ||
        pathname.startsWith('/settings') ||
        pathname.startsWith('/alerts') ||
        pathname.startsWith('/setup')) {
      setIsPublicPage(false);
      return;
    }

    const isPublic = publicPaths.includes(pathname) || pathname === '/' || pathname.startsWith('/tryus') || pathname.startsWith('/features/') || pathname.startsWith('/resources/');
    setIsPublicPage(isPublic);

    if (!isPublic) return;

    const handleScroll = () => {
      setIsVisible(window.scrollY > showAfterScroll);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [showAfterScroll]);

  const handleClick = () => {
    analyticsService.trackCTAClick('Floating CTA', 'bottom-right');
    onClick?.();
  };

  if (!isPublicPage || !isVisible) return null;

  return (
    <button
      onClick={handleClick}
      className="fixed bottom-24 right-6 z-40 px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-medium rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all animate-bounce-subtle flex items-center gap-2"
    >
      <Sparkles className="w-4 h-4" />
      {text}
    </button>
  );
};

// ============================================================================
// INLINE LEAD MAGNET (Appears within content)
// ============================================================================

export const InlineLeadMagnet = ({
  title = " Free Productivity Report",
  description = "See how your team compares to industry benchmarks",
  ctaText = "Get Free Report",
  magnetType = "report",
}) => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    analyticsService.trackFormComplete(`Lead Magnet: ${magnetType}`, { email });
    analyticsService.identify(email, { leadMagnet: magnetType });

    try {
      await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          source: `lead_magnet_${magnetType}`,
          visitorId: analyticsService.visitorId,
        }),
      });
      setIsSubmitted(true);
    } catch (error) {
      console.error('Lead magnet error:', error);
    }
  };

  if (isSubmitted) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
        <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-2" />
        <p className="text-green-800 font-medium">{t('leadCapture.checkInbox')}</p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-6">
      <h4 className="font-semibold text-gray-900 mb-1">{title}</h4>
      <p className="text-sm text-gray-600 dark:text-gray-200 mb-4">{description}</p>
      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('leadCapture.enterEmail')}
          className="flex-1 px-4 py-2 border border-orange-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none text-sm"
          required
        />
        <button
          type="submit"
          className="px-4 py-2 bg-orange-500 text-white font-medium rounded-lg hover:bg-orange-600 transition-colors text-sm whitespace-nowrap"
        >
          {ctaText}
        </button>
      </form>
    </div>
  );
};

// ============================================================================
// SCROLL DEPTH TRACKER (HOC)
// ============================================================================

export const useScrollTracking = () => {
  useEffect(() => {
    let maxScroll = 0;

    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = Math.round((window.scrollY / scrollHeight) * 100);

      if (scrollPercent > maxScroll) {
        maxScroll = scrollPercent;
        analyticsService.trackScroll(scrollPercent);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
};

// Export default component that includes everything
// Only shows on public/marketing pages, not on authenticated dashboard pages
const LeadCaptureSystem = () => {
  const location = useLocation();

  useScrollTracking();

  // Compute synchronously (not via useEffect) so there is zero render-flash when
  // navigating from a public page (shouldShow=true) to /signin or other auth pages.
  // The old useEffect approach left shouldShow=true for one render on the new route,
  // which briefly mounted ExitIntentPopup / FloatingCTA on auth pages and triggered
  // a production crash ("Cannot convert undefined or null to object").
  const publicPaths = ['/', '/pricing', '/features', '/solutions', '/resources', '/partner', '/contact', '/privacy-policy', '/security-compliance', '/screenshot-monitoring', '/productivity-analytics', '/compliance-suite', '/insider-risk', '/tryus', '/compare', '/deployment-guide'];
  const _pathname = location.pathname;
  const isExcluded =
    _pathname.includes('/signin') ||
    _pathname.includes('/signup') ||
    _pathname.includes('/login') ||
    _pathname.includes('/super-admin') ||
    _pathname.includes('/dashboard') ||
    _pathname.startsWith('/employee-') ||
    _pathname.startsWith('/billing') ||
    _pathname.startsWith('/settings') ||
    _pathname.startsWith('/alerts') ||
    _pathname.startsWith('/setup');
  const shouldShow = !isExcluded && (
    publicPaths.includes(_pathname) ||
    _pathname === '/' ||
    _pathname.startsWith('/tryus') ||
    _pathname.startsWith('/features/') ||
    _pathname.startsWith('/deployment-guide') ||
    _pathname.startsWith('/resources/')
  );

  // Don't render anything on protected pages
  if (!shouldShow) {
    return null;
  }

  return (
    <>
      <ExitIntentPopup />
      <FloatingCTA onClick={() => window.location.href = '/contact'} />
    </>
  );
};

export default LeadCaptureSystem;

